"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

interface DragState {
  value: string;
  x: number;
  y: number;
}

interface DragDropContextValue {
  dragValue: string | null;
  startDrag: (value: string, x: number, y: number) => void;
  registerSlot: (id: string, el: HTMLElement) => void;
  unregisterSlot: (id: string) => void;
  highlightSlot: string | null;
}

const DragDropContext = createContext<DragDropContextValue | null>(null);

export function DragDropProvider({
  children,
  onDrop,
}: {
  children: ReactNode;
  onDrop: (value: string, slotId: string | null) => void;
}) {
  const [drag, setDrag] = useState<DragState | null>(null);
  const [highlightSlot, setHighlightSlot] = useState<string | null>(null);
  const slotsRef = useRef(new Map<string, HTMLElement>());
  const onDropRef = useRef(onDrop);

  useEffect(() => {
    onDropRef.current = onDrop;
  }, [onDrop]);

  const registerSlot = useCallback((id: string, el: HTMLElement) => {
    slotsRef.current.set(id, el);
  }, []);

  const unregisterSlot = useCallback((id: string) => {
    slotsRef.current.delete(id);
  }, []);

  const findSlotAt = useCallback((x: number, y: number) => {
    for (const [id, el] of slotsRef.current) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
    }
    return null;
  }, []);

  const startDrag = useCallback((value: string, x: number, y: number) => {
    setDrag({ value, x, y });
  }, []);

  useEffect(() => {
    if (!drag) return;

    const onMove = (e: PointerEvent) => {
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : null));
      setHighlightSlot(findSlotAt(e.clientX, e.clientY));
    };

    const onUp = (e: PointerEvent) => {
      const slotId = findSlotAt(e.clientX, e.clientY);
      onDropRef.current(drag.value, slotId);
      setDrag(null);
      setHighlightSlot(null);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [drag, findSlotAt]);

  const value = useMemo(
    () => ({
      dragValue: drag?.value ?? null,
      startDrag,
      registerSlot,
      unregisterSlot,
      highlightSlot,
    }),
    [drag, startDrag, registerSlot, unregisterSlot, highlightSlot],
  );

  return (
    <DragDropContext.Provider value={value}>
      {children}
      {drag && (
        <div
          className="pointer-events-none fixed z-50 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-2xl bg-indigo-500 text-xl font-extrabold text-white shadow-xl ring-4 ring-indigo-200"
          style={{ left: drag.x, top: drag.y }}
        >
          {drag.value}
        </div>
      )}
    </DragDropContext.Provider>
  );
}

export function useDragDropContext() {
  const ctx = useContext(DragDropContext);
  if (!ctx) throw new Error("useDragDropContext requires DragDropProvider");
  return ctx;
}
